/** Column definitions for the bookings grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget, not per-column grid filters (the page also sets
 * `disableColumnFilter`). Columns hidden by default (custom_id, alt_custom_id, order_ref,
 * supplier_ref, reseller_ref, created_ts, updated_ts, create/update source) are
 * listed in the page's DEFAULT_HIDDEN, NOT a column-level `hide` prop (that is
 * the removed v5 API). The combined Timestamps column is the visible default;
 * the raw Created/Updated columns exist for sorting and for users who prefer
 * single-value columns — they carry the same ±delta popover.
 *
 * `sortable` is only ever true for fields the SERVER can order by
 * (`_ORDERING_MAP` in apiv2/views/console/bookings.py). A sortable header for
 * anything else is a silent no-op — the backend falls back to `id`.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ListAltIcon from '@mui/icons-material/ListAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format as formatDay,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  BOOKING_LIST_EXPANSIONS,
  type BookingAccessGroupRef,
  type BookingGuest,
  type BookingListExpansion,
  type BookingListItem,
  type BookingRoleRef,
  type BookingTag,
  type BookingTenantRef,
  type BookingTourRef,
} from '@/api/bookings';
import {
  CellActionsPopover,
  type CellAction,
} from '@/components/DataGrid/CellActionsPopover';
import { TimestampsCell, TimestampValueCell } from '@/components/DataGrid/TimestampsCell';
import { useSnackbar } from '@/components/Snackbar';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { TenantMark } from '@/components/TenantMark/TenantMark';
import { formatTripDuration } from '@/features/records/time/tripDuration';
import { formatDate } from '@/utils/format';

import { BookingStateChip } from './BookingStateChip';
import {
  END_TYPE_LABEL,
  EndTypeCell,
  PARTICIPANT_LABEL,
  PhaseCell,
  TimezoneCell,
  TripTimeCell,
} from './tripCells';
import {
  ChatCell,
  GuideAppCell,
  GuidesCell,
  RouteCell,
  StartPlaceCell,
  guidesText,
  routeText,
  startPlaceText,
} from './tripExpansionCells';
import { chatStatus, guideAppStatus } from './tripStatus';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

export type TimestampNamespace = 'created' | 'updated';
/** Namespaces where a cell action REPLACES the single top-level condition
 *  (two ANDed date/tour/user/tenant conditions would rarely make sense). */
export type ReplaceableNamespace =
  | TimestampNamespace
  | 'date'
  | 'tour'
  | 'user'
  | 'email'
  | 'phone'
  | 'tenant'
  | 'pax'
  | 'gtu'
  | 'role'
  // The trip's own instants and its phase — each single-valued per booking.
  | 'start'
  | 'end'
  | 'phase'
  // A booking has exactly ONE create/update source, so stacking two source
  // conditions is always empty — these replace, like the rest of this union.
  | 'create_source'
  | 'update_source';
/** Namespaces where a cell click TOGGLES one `<ns>=<value>` condition
 *  (several can meaningfully coexist, like the Tags column — or `agroup`, since
 *  a user can be in many access groups at once). */
export type ToggleableNamespace = 'departure' | 'state' | 'agroup';

/** One top-level condition for onReplaceFilter (null = reset/remove). */
export interface ReplaceFilterCond {
  operator: 'exact' | 'iexact' | 'range';
  value: string | [string, string];
}

type ReplaceFilterFn = (ns: ReplaceableNamespace, cond: ReplaceFilterCond | null) => void;

/** Small copy-to-clipboard affordance for a value already shown as a link. */
function CopyIconButton({ value, label }: { value: string; label: string }) {
  const snackbar = useSnackbar();
  return (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        size="small"
        aria-label={`Copy ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value).then(
            () => snackbar.show(`${label} copied.`, 'success'),
            () => snackbar.show('Could not copy to clipboard.', 'error'),
          );
        }}
        sx={{ p: 0.25 }}
      >
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

/** One contact line (email/phone) — the value opens a quick-actions popover
 *  (send/call, filter, reset), with a copy button next to it. */
function ContactLine({
  value,
  label,
  actions,
}: {
  value: string;
  label: string;
  actions: CellAction[];
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
      <CellActionsPopover
        auto
        actions={actions}
        sx={{
          typography: 'caption',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </CellActionsPopover>
      <CopyIconButton value={value} label={label} />
    </Box>
  );
}

/** Guest name (+ grayed user ID) with email/phone underneath, only when
 *  present. Name, email and phone each open a quick-actions popover: open
 *  the profile / send / call, plus set/reset a filter by that identifier.
 *  The user filter inserts the user ID — the backend's all-digits fallback
 *  resolves it by PK, pinning THIS user, not same-named ones. Each contact
 *  line keeps its inline copy button. */
function GuestCell({
  guest,
  onReplaceFilter,
}: {
  guest: BookingGuest | null;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  if (!guest) return <>—</>;
  const name = `${guest.first_name ?? ''} ${guest.last_name ?? ''}`.trim() || '—';
  const filterActions = (ns: ReplaceableNamespace, value: string, label: string): CellAction[] =>
    onReplaceFilter
      ? [
          {
            key: 'filter',
            label,
            icon: <FilterAltIcon fontSize="small" />,
            onClick: () => onReplaceFilter(ns, { operator: 'iexact', value }),
          },
          {
            key: 'reset',
            label: `Reset ${ns} filter`,
            icon: <FilterAltOffIcon fontSize="small" />,
            onClick: () => onReplaceFilter(ns, null),
          },
        ]
      : [];
  return (
    <Stack spacing={0.25} sx={{ py: 0.75, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
        <CellActionsPopover
          auto
          actions={[
            {
              key: 'open',
              label: 'Open user profile',
              icon: <OpenInNewIcon fontSize="small" />,
              href: `/users/${guest.id}`,
            },
            ...filterActions('user', String(guest.id), `Filter by this user (user=${guest.id})`),
          ]}
          sx={{
            typography: 'body2',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </CellActionsPopover>
        <Typography component="span" variant="caption" color="text.disabled">
          [{guest.id}]
        </Typography>
      </Box>
      {guest.email && (
        <ContactLine
          value={guest.email}
          label="email"
          actions={[
            {
              key: 'send',
              label: 'Send email',
              icon: <EmailIcon fontSize="small" />,
              href: `mailto:${guest.email}`,
            },
            ...filterActions('email', guest.email, 'Filter by this email'),
          ]}
        />
      )}
      {guest.phone && (
        <ContactLine
          value={guest.phone}
          label="phone"
          actions={[
            {
              key: 'call',
              label: 'Call',
              icon: <PhoneIcon fontSize="small" />,
              href: `tel:${guest.phone}`,
            },
            ...filterActions('phone', guest.phone, 'Filter by this phone'),
          ]}
        />
      )}
    </Stack>
  );
}

/** Booking date with a shortcuts popover: same date / ±7-day span / same
 *  ISO week / same month — each REPLACES the top-level `date` condition. */
function DateCell({
  date,
  onReplaceFilter,
}: {
  date: string | null;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  if (!date) return <>—</>;
  if (!onReplaceFilter) return <>{date}</>;
  const d = parseISO(date);
  const day = (x: Date) => formatDay(x, 'yyyy-MM-dd');
  const setRange = (from: Date, to: Date) =>
    onReplaceFilter('date', { operator: 'range', value: [day(from), day(to)] });
  const weekOpts = { weekStartsOn: 1 } as const; // ISO week (Mon–Sun)
  const actions: CellAction[] = [
    {
      key: 'same',
      label: 'Same date',
      tooltip: `Only ${date}`,
      onClick: () => onReplaceFilter('date', { operator: 'exact', value: date }),
    },
    {
      key: 'plus7',
      label: '+7 days',
      tooltip: `${date} .. ${day(addDays(d, 7))}`,
      onClick: () => setRange(d, addDays(d, 7)),
    },
    {
      key: 'minus7',
      label: '-7 days',
      tooltip: `${day(addDays(d, -7))} .. ${date}`,
      onClick: () => setRange(addDays(d, -7), d),
    },
    {
      key: 'week',
      label: 'Same week',
      tooltip: `${day(startOfWeek(d, weekOpts))} .. ${day(endOfWeek(d, weekOpts))}`,
      onClick: () => setRange(startOfWeek(d, weekOpts), endOfWeek(d, weekOpts)),
    },
    {
      key: 'month',
      label: 'Same month',
      tooltip: `${day(startOfMonth(d))} .. ${day(endOfMonth(d))}`,
      onClick: () => setRange(startOfMonth(d), endOfMonth(d)),
    },
    {
      key: 'reset',
      label: 'Reset date filter',
      icon: <FilterAltOffIcon fontSize="small" />,
      onClick: () => onReplaceFilter('date', null),
    },
  ];
  return (
    <CellActionsPopover auto actions={actions}>
      {date}
    </CellActionsPopover>
  );
}

/** Tour name with a popover: open the tour details page (not built yet — a
 *  snackbar says so) or set/reset a `tour=<id>` filter. The ID round-trips
 *  through the query DSL as an iexact value; the backend's all-digits
 *  fallback resolves it against the tour PK, so the filter pins THIS tour,
 *  not every same-named tour. */
function TourCell({
  tour,
  onReplaceFilter,
}: {
  tour: BookingTourRef | null;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  if (!tour) return <>—</>;
  const actions: CellAction[] = [
    {
      key: 'details',
      label: 'Open tour details',
      icon: <OpenInNewIcon fontSize="small" />,
      href: `/tours/${tour.id}`,
    },
    ...(onReplaceFilter
      ? [
          {
            key: 'filter',
            label: `Filter by this tour (tour=${tour.id})`,
            icon: <FilterAltIcon fontSize="small" />,
            onClick: () =>
              onReplaceFilter('tour', { operator: 'iexact', value: String(tour.id) }),
          },
          {
            key: 'reset',
            label: 'Reset tour filter',
            icon: <FilterAltOffIcon fontSize="small" />,
            onClick: () => onReplaceFilter('tour', null),
          },
        ]
      : []),
  ];
  return (
    <CellActionsPopover auto actions={actions}>
      {tour.name}
    </CellActionsPopover>
  );
}

/** Pax count with a shortcuts popover of ABSOLUTE values: 1–8 and 9+.
 *  Picking a filter ALWAYS takes two clicks: the first click arms a bound
 *  (nothing is applied yet); the second click applies — the same number
 *  twice means exactly that count (9+ twice = at least 9), two different
 *  numbers mean the inclusive range between them (a 9+ bound makes it
 *  open-ended, e.g. "3+"). While armed, hovering a button live-highlights
 *  the range the completing click would select. The armed bound resets
 *  whenever the popover closes. */
function PaxCell({
  count,
  onReplaceFilter,
}: {
  count: number | null;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  const [rangeStart, setRangeStart] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const text = count ?? '—';
  if (!onReplaceFilter) return <>{text}</>;

  const paxValue = (a: number, b: number): string => {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    if (lo === hi) return lo >= 9 ? '9+' : String(lo);
    return hi >= 9 ? `${lo}+` : `${lo}-${hi}`;
  };
  const disarm = () => {
    setRangeStart(null);
    setHovered(null);
  };
  const pick = (n: number) => {
    if (rangeStart === null) {
      setRangeStart(n);
      return;
    }
    onReplaceFilter('pax', { operator: 'exact', value: paxValue(rangeStart, n) });
    disarm();
  };
  // The highlight preview: just the armed bound, or — once a second button
  // is hovered — every value the completing click would cover.
  const isActive = (n: number): boolean => {
    if (rangeStart === null) return false;
    if (hovered === null) return n === rangeStart;
    const [lo, hi] = rangeStart <= hovered ? [rangeStart, hovered] : [hovered, rangeStart];
    return n >= lo && n <= hi;
  };
  const actions: CellAction[] = [
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
      const label = n >= 9 ? '9+' : String(n);
      return {
        key: String(n),
        label,
        tooltip:
          rangeStart === null
            ? `Pax from ${label}`
            : `Filter pax ${paxValue(rangeStart, n)}`,
        onClick: () => pick(n),
        onHover: () => setHovered(n),
        // The arming click keeps the popover open for the completing one.
        keepOpen: rangeStart === null,
        active: isActive(n),
      };
    }),
    {
      key: 'reset',
      label: 'Reset pax filter',
      icon: <FilterAltOffIcon fontSize="small" />,
      onClick: () => {
        onReplaceFilter('pax', null);
        disarm();
      },
      onHover: () => setHovered(null),
    },
  ];
  return (
    // Deliberately NOT `auto`: hover mode's close-on-mouseleave timer races
    // the two-click flow — the arming click re-renders the panel (title and
    // button variants change), the Paper resizes under the pointer, and the
    // resulting mouseleave closes the popover before the completing click.
    // The modal Popover (click-to-open, backdrop) has no such race.
    // The title is deliberately STATIC — swapping it after the arming click
    // would resize the panel and shift the buttons under the pointer, right
    // between the two clicks. The armed bound is shown by its highlight.
    <CellActionsPopover
      actions={actions}
      onClose={disarm}
      title="Click two values — the same twice for one count, different for a range"
    >
      {text}
    </CellActionsPopover>
  );
}

/** Tenant name with a quick-actions popover (open the tour-operator page,
 *  set/reset a `tenant=<code>` filter) and a badge line below it (when
 *  applicable — none for a plain production tenant): Franchise/Licensee and
 *  the Sandbox mark (D8), so the column doesn't need to widen to fit them. */
function TenantCell({
  tenant,
  onReplaceFilter,
}: {
  tenant: BookingTenantRef | null;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  if (!tenant) return <>—</>;
  const isLicensee = tenant.parent_id != null;
  const isFranchise = !isLicensee && tenant.is_franchise;
  const isSandbox = tenant.environment === 'sandbox';
  const actions: CellAction[] = [
    {
      key: 'open',
      label: 'Open tour operator',
      icon: <OpenInNewIcon fontSize="small" />,
      href: `/tour-operators/${tenant.id}`,
    },
    ...(onReplaceFilter
      ? [
          {
            key: 'filter',
            label: `Filter by this tenant (tenant=${tenant.code})`,
            icon: <FilterAltIcon fontSize="small" />,
            onClick: () => onReplaceFilter('tenant', { operator: 'iexact', value: tenant.code }),
          },
          {
            key: 'reset',
            label: 'Reset tenant filter',
            icon: <FilterAltOffIcon fontSize="small" />,
            onClick: () => onReplaceFilter('tenant', null),
          },
        ]
      : []),
  ];
  return (
    <Stack spacing={0.25} sx={{ py: 0.75, minWidth: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <CellActionsPopover
          auto
          actions={actions}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {tenant.name}
        </CellActionsPopover>
      </Box>
      {(isLicensee || isFranchise || isSandbox) && (
        <Stack direction="row" spacing={0.5} sx={{ alignSelf: 'flex-start' }}>
          {isLicensee && (
            <Chip size="small" color="info" label="Licensee" sx={{ height: 18, fontSize: 11 }} />
          )}
          {isFranchise && (
            <Chip
              size="small"
              color="primary"
              label="Franchise"
              sx={{ height: 18, fontSize: 11 }}
            />
          )}
          <TenantMark environment={tenant.environment} />
        </Stack>
      )}
    </Stack>
  );
}

/** A `filter by this value` / `reset` pair for a REPLACE-semantics namespace.
 *  `[]` when the page wired no onReplaceFilter (cell renders as plain text). */
function replaceActions(
  ns: ReplaceableNamespace,
  value: string,
  filterLabel: string,
  onReplaceFilter?: ReplaceFilterFn | undefined,
): CellAction[] {
  if (!onReplaceFilter) return [];
  return [
    {
      key: 'filter',
      label: filterLabel,
      icon: <FilterAltIcon fontSize="small" />,
      onClick: () => onReplaceFilter(ns, { operator: 'iexact', value }),
    },
    {
      key: 'reset',
      label: `Reset ${ns} filter`,
      icon: <FilterAltOffIcon fontSize="small" />,
      onClick: () => onReplaceFilter(ns, null),
    },
  ];
}

/** The booking's real database primary key, with a popover to open it or pin
 *  the grid to it. The namespace is `gtu`, NOT `id` — `id` is `custom_id`, the
 *  booking's identifier in the SOURCE booking system. */
function BookingIdCell({
  id,
  onReplaceFilter,
}: {
  id: number;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  return (
    <CellActionsPopover
      auto
      actions={[
        {
          // Deliberately NOT "Open booking" — that is the leftmost icon
          // column's label, and two identically-named affordances make the
          // grid ambiguous to keyboard and screen-reader users alike.
          key: 'open',
          label: 'Open booking details',
          icon: <OpenInNewIcon fontSize="small" />,
          href: `/bookings/${id}`,
        },
        ...replaceActions('gtu', String(id), `Filter by this booking (gtu=${id})`, onReplaceFilter),
      ]}
      sx={mono}
    >
      {id}
    </CellActionsPopover>
  );
}

/** The staff role on the booking (null for ordinary guest bookings). The filter
 *  inserts the role ID — the backend's all-digits fallback resolves it by PK,
 *  pinning THIS role rather than every same-named role across tenants. */
function RoleCell({
  role,
  onReplaceFilter,
}: {
  role: BookingRoleRef | null;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  if (!role) return <>—</>;
  const actions = replaceActions(
    'role',
    String(role.id),
    `Filter by this role (role=${role.id})`,
    onReplaceFilter,
  );
  if (actions.length === 0) return <>{role.name}</>;
  return (
    <CellActionsPopover
      auto
      actions={actions}
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {role.name}
    </CellActionsPopover>
  );
}

/** Styles that undo CellActionsPopover's dotted-underline trigger — a Chip
 *  trigger carries its own affordance, and an underline through it reads as a
 *  rendering bug. Both keys must be overridden: the spread replaces whole
 *  values, so leaving `&:hover` out keeps the base solid-underline rule. */
const CHIP_TRIGGER_SX = {
  display: 'inline-flex',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'none' },
} as const;

/** The EFFECTIVE access groups of the booking's user (direct membership, roles,
 *  or the tenant guest group — see the backend's `agroup` namespace, which
 *  matches this exact set). Every chip opens a popover: jump to the group's RBAC
 *  page, or toggle an `agroup=<name>` filter on/off. Several access groups can
 *  hold at once, so these TOGGLE rather than replace. */
function AccessGroupsCell({
  agroups,
  onToggleFilter,
  isFilterActive,
}: {
  agroups: BookingAccessGroupRef[] | undefined;
  onToggleFilter?: ((ns: ToggleableNamespace, value: string) => void) | undefined;
  isFilterActive?: ((ns: ToggleableNamespace, value: string) => boolean) | undefined;
}) {
  if (!agroups || agroups.length === 0) return <>—</>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.75, minWidth: 0 }}>
      {agroups.map((ag) => {
        const active = isFilterActive?.('agroup', ag.name) ?? false;
        const actions: CellAction[] = [
          {
            key: 'open',
            label: 'Open access group',
            icon: <OpenInNewIcon fontSize="small" />,
            href: `/rbac/access-groups/${ag.id}`,
          },
          ...(onToggleFilter
            ? [
                {
                  key: 'toggle',
                  label: active
                    ? `Remove this access-group filter (agroup=${ag.name})`
                    : `Filter by this access group (agroup=${ag.name})`,
                  icon: active ? (
                    <FilterAltOffIcon fontSize="small" />
                  ) : (
                    <FilterAltIcon fontSize="small" />
                  ),
                  onClick: () => onToggleFilter('agroup', ag.name),
                },
              ]
            : []),
        ];
        return (
          <CellActionsPopover key={ag.id} auto actions={actions} sx={CHIP_TRIGGER_SX}>
            <Chip
              size="small"
              label={ag.name}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              sx={{ height: 20, fontSize: 12, '& .MuiChip-label': { px: 0.75 } }}
            />
          </CellActionsPopover>
        );
      })}
    </Box>
  );
}

/** One create/update source label with a filter/reset popover. Single-valued
 *  per booking, so it replaces rather than stacks. */
function SourceCell({
  ns,
  value,
  onReplaceFilter,
}: {
  ns: 'create_source' | 'update_source';
  value: string | null | undefined;
  onReplaceFilter?: ReplaceFilterFn | undefined;
}) {
  if (!value) return <>—</>;
  const actions = replaceActions(
    ns,
    value,
    `Filter by this source (${ns}=${value})`,
    onReplaceFilter,
  );
  if (actions.length === 0) return <>{value}</>;
  return (
    <CellActionsPopover auto actions={actions}>
      {value}
    </CellActionsPopover>
  );
}

/** Departure label with a two-action popover: toggle the plain
 *  `departure=<name>` filter (the original click-the-value behaviour), or
 *  toggle the whole MANIFEST — tenant + tour + date + departure + booked — which
 *  is the set of rows that would appear on the guide's sheet for this run. */
function DepartureCell({
  row,
  onToggleFilter,
  isFilterActive,
  onToggleManifest,
  isManifestActive,
}: {
  row: BookingListItem;
  onToggleFilter?: ((ns: ToggleableNamespace, value: string) => void) | undefined;
  isFilterActive?: ((ns: ToggleableNamespace, value: string) => boolean) | undefined;
  onToggleManifest?: ((row: BookingListItem) => void) | undefined;
  isManifestActive?: ((row: BookingListItem) => boolean) | undefined;
}) {
  const dep = row.departure;
  if (!dep) return <>—</>;
  const depActive = isFilterActive?.('departure', dep.name) ?? false;
  const manifestActive = isManifestActive?.(row) ?? false;
  const actions: CellAction[] = [
    ...(onToggleFilter
      ? [
          {
            key: 'departure',
            label: depActive
              ? `Remove this departure filter (departure=${dep.name})`
              : `Filter by this departure (departure=${dep.name})`,
            icon: depActive ? (
              <FilterAltOffIcon fontSize="small" />
            ) : (
              <FilterAltIcon fontSize="small" />
            ),
            onClick: () => onToggleFilter('departure', dep.name),
          },
        ]
      : []),
    ...(onToggleManifest
      ? [
          {
            key: 'manifest',
            label: manifestActive
              ? 'Remove this manifest filter'
              : 'Filter by this manifest (tenant + tour + date + departure + booked)',
            icon: manifestActive ? (
              <FilterAltOffIcon fontSize="small" />
            ) : (
              <ListAltIcon fontSize="small" />
            ),
            onClick: () => onToggleManifest(row),
          },
        ]
      : []),
  ];
  if (actions.length === 0) return <>{dep.name}</>;
  return (
    <CellActionsPopover auto actions={actions}>
      {dep.name}
    </CellActionsPopover>
  );
}

/** The row expansion (`?expand=`) each expansion column reads. A column not listed here
 *  reads only always-on fields. */
export const COLUMN_EXPANSIONS: Readonly<Record<string, BookingListExpansion>> = {
  guides: 'guides',
  start_place: 'where',
  route: 'where',
  guide_app: 'staff_visibility',
  chat: 'chat',
};

/** The expansions the visible columns read, in the canonical order — what the grid asks
 *  the list for, so the backend resolves nothing nobody looks at. A column absent from the
 *  visibility model is visible (MUI's rule). */
export function expansionsForColumns(visibility: GridColumnVisibilityModel): BookingListExpansion[] {
  const wanted = new Set(
    Object.entries(COLUMN_EXPANSIONS)
      .filter(([field]) => visibility[field] !== false)
      .map(([, expansion]) => expansion),
  );
  return BOOKING_LIST_EXPANSIONS.filter((e) => wanted.has(e));
}

export interface BuildColumnsOptions {
  showTenant: boolean;
  /** Toggle a `tag=<name>` filter — wired to the QuerySearch widget's tree by
   *  the page. Omit to render the Tags column's chips as non-interactive. */
  onToggleTag?: (tag: BookingTag) => void;
  isTagActive?: (tag: BookingTag) => boolean;
  /** Replace (or, with null, remove) the single top-level condition of a
   *  single-valued namespace — Date shortcuts, Tour filter, Timestamps ±deltas.
   *  Omit to render those cells without filter actions. */
  onReplaceFilter?: ReplaceFilterFn;
  /** Toggle one `<ns>=<value>` (iexact) condition — Departure/State clicks and
   *  the Access groups chips. Omit to render those cells as non-interactive. */
  onToggleFilter?: (ns: ToggleableNamespace, value: string) => void;
  /** Whether one `<ns>=<value>` (iexact) condition is currently in the query.
   *  Drives the on/off wording + icon of the toggling cell actions. */
  isFilterActive?: (ns: ToggleableNamespace, value: string) => boolean;
  /** Toggle the whole manifest of a row (tenant + tour + date + departure +
   *  booked state) — the Departure popover's second action. */
  onToggleManifest?: (row: BookingListItem) => void;
  isManifestActive?: (row: BookingListItem) => boolean;
}

export function buildColumns({
  showTenant,
  onToggleTag,
  isTagActive,
  onReplaceFilter,
  onToggleFilter,
  isFilterActive,
  onToggleManifest,
  isManifestActive,
}: BuildColumnsOptions): GridColDef<BookingListItem>[] {
  const cols: GridColDef<BookingListItem>[] = [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (p) => (
        <Tooltip title="Open booking">
          <IconButton
            size="small"
            component={RouterLink}
            to={`/bookings/${p.row.id}`}
            aria-label="Open booking"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      // The real database primary key. Server-sortable (`id` is in
      // _ORDERING_MAP) and the grid's default ordering key.
      field: 'id',
      headerName: 'ID',
      width: 100,
      filterable: false,
      renderCell: (p) => <BookingIdCell id={p.row.id} onReplaceFilter={onReplaceFilter} />,
    },
    {
      field: 'guest',
      headerName: 'Guest',
      flex: 1.3,
      minWidth: 220,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        row.guest ? `${row.guest.first_name ?? ''} ${row.guest.last_name ?? ''}`.trim() : '',
      renderCell: (p) => <GuestCell guest={p.row.guest} onReplaceFilter={onReplaceFilter} />,
    },
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tenant?.name ?? '—',
      renderCell: (p) => <TenantCell tenant={p.row.tenant} onReplaceFilter={onReplaceFilter} />,
    });
  }

  cols.push(
    {
      field: 'tour',
      headerName: 'Tour',
      flex: 1,
      minWidth: 160,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tour?.name ?? '—',
      renderCell: (p) => <TourCell tour={p.row.tour} onReplaceFilter={onReplaceFilter} />,
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      filterable: false,
      valueGetter: (_v, row) => row.date ?? '—',
      renderCell: (p) => <DateCell date={p.row.date} onReplaceFilter={onReplaceFilter} />,
    },
    {
      field: 'departure',
      headerName: 'Departure',
      width: 120,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.departure?.name ?? '—',
      renderCell: (p) => (
        <DepartureCell
          row={p.row}
          onToggleFilter={onToggleFilter}
          isFilterActive={isFilterActive}
          onToggleManifest={onToggleManifest}
          isManifestActive={isManifestActive}
        />
      ),
    },
    {
      field: 'booking_state',
      headerName: 'State',
      width: 130,
      filterable: false,
      sortable: false,
      renderCell: (p) => {
        const chip = (
          <BookingStateChip
            booking_state={p.row.booking_state}
            booking_state_display={p.row.booking_state_display}
          />
        );
        if (!onToggleFilter || !p.row.booking_state_display) return chip;
        return (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFilter('state', p.row.booking_state_display);
            }}
            sx={{ cursor: 'pointer' }}
          >
            {chip}
          </Box>
        );
      },
    },
    {
      field: 'people_count',
      headerName: 'Pax',
      width: 70,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <PaxCell count={p.row.people_count} onReplaceFilter={onReplaceFilter} />
      ),
    },
    {
      field: 'custom_id',
      headerName: 'Custom ID',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.custom_id || '—'}</Box>,
    },
    {
      // The booking's second id in its booking system (Ventrata's native id).
      field: 'alt_custom_id',
      headerName: 'Alt ID',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.alt_custom_id || '—'}</Box>,
    },
    {
      field: 'order_ref',
      headerName: 'Order ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.order_ref || '—'}</Box>,
    },
    {
      // The operator's code for the departure INSTANCE -- the 'Departure'
      // column above is a time-of-day tag, which cannot tell two departures
      // of one tour at the same instant apart. sortable: unlike its
      // neighbours, departure_key IS in the server's _ORDERING_MAP.
      field: 'departure_key',
      headerName: 'Departure code',
      width: 170,
      filterable: false,
      sortable: true,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.departure_key || '—'}</Box>,
    },
    {
      field: 'supplier_ref',
      headerName: 'Supplier ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.supplier_ref || '—'}</Box>,
    },
    {
      field: 'reseller_ref',
      headerName: 'Reseller ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.reseller_ref || '—'}</Box>,
    },
    {
      // Not in the server ordering whitelist — display-only.
      field: 'integration',
      headerName: 'Integration',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => (p.row.integration ? p.row.integration : '—'),
    },
    {
      field: 'affiliate_ext_id',
      headerName: 'Affiliate ext ID',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.affiliate_ext_id || '—'}</Box>,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      minWidth: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <TagBadgeSet tags={p.row.tags} onToggleTag={onToggleTag} isTagActive={isTagActive} />
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 140,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.role?.name ?? '—',
      renderCell: (p) => <RoleCell role={p.row.role} onReplaceFilter={onReplaceFilter} />,
    },
    {
      // Who the person is on the booking: a guide (the role broadcasts the user's
      // position), other staff, an observer or a guest. No filter namespace.
      field: 'participant_kind',
      headerName: 'Participant',
      width: 110,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => PARTICIPANT_LABEL[row.participant_kind],
    },
    // ---- Expansion columns (D5): each reads a row property the list returns only
    // when asked (`expand=`); BookingsPage asks for exactly the visible ones
    // (COLUMN_EXPANSIONS). None is sortable or has a filter namespace.
    {
      field: 'guides',
      headerName: 'Guides',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => guidesText(row),
      renderCell: (p) => <GuidesCell row={p.row} />,
    },
    {
      // Is the booking in front of the guide in the guide app? Staff rows only.
      field: 'guide_app',
      headerName: 'Guide app',
      width: 240,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        row.staff_visibility ? guideAppStatus(row.staff_visibility, row.timezone).text : '',
      renderCell: (p) => <GuideAppCell row={p.row} />,
    },
    {
      field: 'chat',
      headerName: 'Chat',
      width: 240,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => (row.chat ? chatStatus(row.chat, row.timezone).text : ''),
      renderCell: (p) => <ChatCell row={p.row} />,
    },
    {
      field: 'agroups',
      headerName: 'Access groups',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      // Export/copy needs a flat string — the renderer draws chips.
      valueGetter: (_v, row) => (row.agroups ?? []).map((ag) => ag.name).join(', ') || '—',
      renderCell: (p) => (
        <AccessGroupsCell
          agroups={p.row.agroups}
          onToggleFilter={onToggleFilter}
          isFilterActive={isFilterActive}
        />
      ),
    },
    {
      field: 'booking_ts',
      headerName: 'Booked at',
      width: 170,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.booking_ts ? (
          <Tooltip title={p.row.booking_ts} placement="top" arrow>
            <Box component="span">{formatDate(p.row.booking_ts)}</Box>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      // start_ts (trip start) IS in the server ordering whitelist — sortable. Shown in
      // the trip's own zone, named under it (never the browser's, never unlabelled).
      field: 'start_ts',
      headerName: 'Trip start',
      width: 180,
      filterable: false,
      renderCell: (p) => (
        <TripTimeCell ns="start" iso={p.row.start_ts} row={p.row} onReplaceFilter={onReplaceFilter} />
      ),
    },
    {
      // The computed end (start + the tour's length, or a stay's own check-out).
      // Not in the server ordering whitelist.
      field: 'end_ts',
      headerName: 'Trip end',
      width: 180,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <TripTimeCell ns="end" iso={p.row.end_ts} row={p.row} onReplaceFilter={onReplaceFilter} />
      ),
    },
    {
      field: 'end_ts_type',
      headerName: 'End type',
      width: 110,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => END_TYPE_LABEL[row.end_ts_type],
      renderCell: (p) => <EndTypeCell row={p.row} />,
    },
    {
      field: 'timezone',
      headerName: 'Timezone',
      width: 170,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.timezone ?? '',
      renderCell: (p) => <TimezoneCell row={p.row} />,
    },
    {
      field: 'duration_s',
      headerName: 'Duration',
      width: 100,
      filterable: false,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => formatTripDuration(row.duration_s),
    },
    {
      // past / active / future / undated, by the spine's rule; filterable through
      // the `phase` namespace from the cell.
      field: 'phase',
      headerName: 'Phase',
      width: 110,
      filterable: false,
      sortable: false,
      renderCell: (p) => <PhaseCell phase={p.row.phase} onReplaceFilter={onReplaceFilter} />,
    },
    {
      // Where the booking starts (expansion `where`).
      field: 'start_place',
      headerName: 'Start',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => startPlaceText(row),
      renderCell: (p) => <StartPlaceCell row={p.row} />,
    },
    {
      // Where the trip goes (expansion `where`).
      field: 'route',
      headerName: 'Route',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => routeText(row),
      renderCell: (p) => <RouteCell row={p.row} />,
    },
    {
      // Keeps the raw `survey_state` field name (so saved column presets and the
      // server ordering key stay valid — same as `booking_state` above) but
      // renders the LABEL. Rendering the field itself showed users a bare "3".
      field: 'survey_state',
      headerName: 'Survey',
      width: 130,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.survey_state_display || '—',
      renderCell: (p) => p.row.survey_state_display || '—',
    },
    {
      field: 'public_id',
      headerName: 'Public ID',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.public_id || '—'}</Box>,
    },
    {
      field: 'branchio_link',
      headerName: 'Branch.io link',
      width: 220,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.branchio_link || '—'}</Box>,
    },
    {
      field: 'mytrips_display',
      headerName: 'My Trips',
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (p) => (p.row.mytrips_display ? '✓' : '—'),
    },
    {
      field: 'exclude_from_chat',
      headerName: 'No chat',
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (p) => (p.row.exclude_from_chat ? '✓' : '—'),
    },
    {
      field: 'exclude_from_group_chat',
      headerName: 'No group chat',
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (p) => (p.row.exclude_from_group_chat ? '✓' : '—'),
    },
    {
      field: 'create_source_display',
      headerName: 'Create source',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <SourceCell
          ns="create_source"
          value={p.row.create_source_display}
          onReplaceFilter={onReplaceFilter}
        />
      ),
    },
    {
      field: 'update_source_display',
      headerName: 'Update source',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <SourceCell
          ns="update_source"
          value={p.row.update_source_display}
          onReplaceFilter={onReplaceFilter}
        />
      ),
    },
    {
      field: 'timestamps',
      headerName: 'Timestamps',
      width: 240,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <TimestampsCell
          created={p.row.created_ts}
          updated={p.row.updated_ts}
          createdSource={p.row.create_source_display}
          updatedSource={p.row.update_source_display}
          onReplaceFilter={onReplaceFilter}
          {...(onReplaceFilter
            ? {
                onSourceFilter: (ns: 'create_source' | 'update_source', value: string | null) =>
                  onReplaceFilter(ns, value === null ? null : { operator: 'iexact', value }),
              }
            : {})}
        />
      ),
    },
    {
      // Raw sortable Created/Updated (hidden by default) — same ±delta popover
      // as the combined Timestamps column, minus the label/source lines.
      field: 'created_ts',
      headerName: 'Created',
      width: 170,
      filterable: false,
      renderCell: (p) => (
        <TimestampValueCell ns="created" iso={p.row.created_ts} onReplaceFilter={onReplaceFilter} />
      ),
    },
    {
      field: 'updated_ts',
      headerName: 'Updated',
      width: 170,
      filterable: false,
      renderCell: (p) => (
        <TimestampValueCell ns="updated" iso={p.row.updated_ts} onReplaceFilter={onReplaceFilter} />
      ),
    },
  );

  return cols;
}
